/**
 * Painel moderno de compartilhamento de roteiro (WhatsApp / E-mail / link).
 * Usado pelo LiveMap e pelo RouteModal.
 */

function escHtml(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatShareDt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
}

function buildShareMessage(url, technicianName) {
  const name = String(technicianName || '').trim() || 'técnico';
  return `Roteiro de ${name}\n${url}`;
}

async function copyTextToClipboard(text, inputEl) {
  let ok = false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      ok = true;
    }
  } catch {
    ok = false;
  }
  if (!ok && inputEl) {
    try {
      inputEl.focus();
      inputEl.select();
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
  }
  return ok;
}

export function closeRoadmapSharePanel() {
  document.querySelectorAll('[data-tpm="share-link-modal"]').forEach((el) => el.remove());
}

/**
 * @param {{
 *   url: string,
 *   reused?: boolean,
 *   validFrom?: string|null,
 *   expiresAt?: string|null,
 *   technicianName?: string,
 *   onPrint?: (() => void)|null,
 *   showPrint?: boolean,
 * }} info
 */
export function openRoadmapSharePanel(info) {
  closeRoadmapSharePanel();
  const url = String(info?.url || '').trim();
  if (!url) return;

  const overlay = document.createElement('div');
  overlay.className = 'tpm-share-modal-overlay';
  overlay.setAttribute('data-tpm', 'share-link-modal');
  overlay.setAttribute('role', 'presentation');

  const reused = !!info.reused;
  const title = 'Compartilhar';
  const msg = reused
    ? 'O roteiro não mudou. O link anterior continua válido.'
    : 'Link público do roteiro pronto para enviar.';
  const shareText = buildShareMessage(url, info.technicianName);
  const mailSubject = encodeURIComponent(
    `Roteiro — ${String(info.technicianName || 'técnico').trim() || 'técnico'}`
  );
  const mailBody = encodeURIComponent(shareText);
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailHref = `mailto:?subject=${mailSubject}&body=${mailBody}`;
  const showPrint = info.showPrint !== false && typeof info.onPrint === 'function';

  const validLine = info.validFrom
    ? `<p class="tpm-share-modal-meta">Disponível a partir de <strong>${escHtml(formatShareDt(info.validFrom))}</strong> (Brasília)</p>`
    : '';
  const expLine = info.expiresAt
    ? `<p class="tpm-share-modal-meta">Expira em <strong>${escHtml(formatShareDt(info.expiresAt))}</strong> (Brasília)</p>`
    : '';

  const waSvg =
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">` +
    `<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>` +
    `</svg>`;
  const mailSvg =
    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
    `<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/>` +
    `</svg>`;

  const footerHtml = showPrint
    ? `<div class="tpm-share-modal-footer">` +
      `<button type="button" class="tpm-share-modal-pdf" data-tpm="share-link-pdf">PDF / Imprimir</button>` +
      `</div>`
    : '';

  overlay.innerHTML =
    `<div class="tpm-share-modal" role="dialog" aria-modal="true" aria-labelledby="tpm-share-modal-title">` +
    `<div class="tpm-share-modal-head">` +
    `<h3 id="tpm-share-modal-title" class="tpm-share-modal-title">${escHtml(title)}</h3>` +
    `<button type="button" class="tpm-share-modal-x" data-tpm="share-link-close" aria-label="Fechar">` +
    `<svg width="14" height="14" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4" fill="none"><path d="M5 5l14 14M19 5L5 19"></path></svg>` +
    `</button></div>` +
    `<p class="tpm-share-modal-msg">${escHtml(msg)}</p>` +
    `<div class="tpm-share-channels" role="list">` +
    `<a class="tpm-share-channel" role="listitem" data-tpm="share-link-whatsapp" href="${escHtml(waHref)}" target="_blank" rel="noopener noreferrer">` +
    `<span class="tpm-share-channel-icon is-whatsapp">${waSvg}</span>` +
    `<span class="tpm-share-channel-label">WhatsApp</span></a>` +
    `<a class="tpm-share-channel" role="listitem" data-tpm="share-link-email" href="${escHtml(mailHref)}">` +
    `<span class="tpm-share-channel-icon is-email">${mailSvg}</span>` +
    `<span class="tpm-share-channel-label">E-mail</span></a>` +
    `</div>` +
    `<div class="tpm-share-modal-url-row">` +
    `<input type="text" class="tpm-share-modal-url" data-tpm="share-link-input" readonly value="${escHtml(url)}" />` +
    `<button type="button" class="tpm-share-modal-copy" data-tpm="share-link-copy">` +
    `<span class="tpm-share-modal-copy-label">Copiar</span></button>` +
    `</div>` +
    validLine +
    expLine +
    footerHtml +
    `</div>`;

  const onOverlayClick = (e) => {
    if (e.target === overlay) closeRoadmapSharePanel();
  };

  const onKey = (e) => {
    if (e.key === 'Escape') closeRoadmapSharePanel();
  };

  const onClick = async (e) => {
    const closeBtn = e.target.closest('[data-tpm="share-link-close"]');
    if (closeBtn) {
      e.preventDefault();
      closeRoadmapSharePanel();
      return;
    }
    const pdfBtn = e.target.closest('[data-tpm="share-link-pdf"]');
    if (pdfBtn) {
      e.preventDefault();
      closeRoadmapSharePanel();
      if (typeof info.onPrint === 'function') info.onPrint();
      return;
    }
    const copyBtn = e.target.closest('[data-tpm="share-link-copy"]');
    if (copyBtn) {
      e.preventDefault();
      const input = overlay.querySelector('[data-tpm="share-link-input"]');
      const text = input?.value || url;
      const ok = await copyTextToClipboard(text, input);
      copyBtn.classList.toggle('is-copied', ok);
      const label = copyBtn.querySelector('.tpm-share-modal-copy-label');
      if (label) label.textContent = ok ? 'Copiado!' : 'Falha';
      window.setTimeout(() => {
        if (!copyBtn.isConnected) return;
        copyBtn.classList.remove('is-copied');
        if (label) label.textContent = 'Copiar';
      }, 2200);
    }
  };

  overlay.addEventListener('click', onClick);
  overlay.addEventListener('click', onOverlayClick);
  document.addEventListener('keydown', onKey);
  const prevRemove = overlay.remove.bind(overlay);
  overlay.remove = () => {
    document.removeEventListener('keydown', onKey);
    prevRemove();
  };

  document.body.appendChild(overlay);
  overlay.querySelector('[data-tpm="share-link-copy"]')?.focus();
}

export function routeShareFingerprint(day, forecast, route) {
  return `${day}|${forecast || ''}|${(route || []).join(',')}`;
}
